// Copyright 2016 Sorama B.V.

import { autoinject, BindingEngine, Disposable } from "aurelia-framework";
import { UserSettingsServiceError } from "errors/UserSettingsServiceError";
import { StringExtensions } from "extensions/StringExtensions";
import { getAllUserSettings, getUserSettingSettings } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { ParserCatalog } from "parsers/ParserCatalog";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";

@autoinject
export class UserSettingsService implements IUserSettingsService {
    private bindingEngine: BindingEngine;

    private static keyFormatter = "{0}-{1}";
    private localStorageAvailable: boolean;

    constructor(bindingEngine: BindingEngine) {
        this.bindingEngine = bindingEngine;
        this.localStorageAvailable = typeof Storage !== "undefined";
    }

    public bindUserSettingsProperty(object: object): Promise<Disposable> {
        if (!object) return Promise.resolve(<Disposable>{ dispose: () => {} });

        const promises: Promise<Disposable>[] = [];

        for (const property of getAllUserSettings(object)) {
            promises.push(this.bind(property.settings.settingsModuleKey, property.propertyName, object));
        }

        getAllUserSettings(object);

        return Promise.all(promises).then((disposables) => {
            return <Disposable>{
                dispose: () => {
                    disposables.forEach((x) => x.dispose());
                },
            };
        });
    }

    public bind(groupKey: string, propertyName: string, object: unknown): Promise<Disposable> {
        // Check if the property is available in storage
        return this.hasKey(groupKey, propertyName)
            .then((hasKey) => (hasKey ? this.get(groupKey, propertyName) : Promise.resolve(undefined)))
            .then((value: string) => {
                if (value != undefined) {
                    // Assign value
                    try {
                        const newVal = this.parseFromString(value, object, propertyName);

                        // Check if object is valid
                        if (newVal !== undefined) {
                            object[propertyName] = newVal;
                            value = newVal as string;
                        }
                    } catch {
                        // Invalid entry, so delete and continue
                        value = undefined;
                    }
                }

                // No valid value known. Store default value of object, if present
                if (value === undefined && object[propertyName] !== undefined && object[propertyName] !== null) {
                    // Save initial value
                    const v = this.parseToString(object[propertyName], object, propertyName);
                    this.set(groupKey, propertyName, v);
                }

                // Register observer
                const observer = this.bindingEngine.propertyObserver(object, propertyName);
                return observer.subscribe((newValue: unknown, oldValue: unknown) => {
                    const convertedValue = this.parseToString(newValue, object, propertyName);

                    if (convertedValue !== undefined) {
                        this.set(groupKey, propertyName, <string>convertedValue);
                    }
                });
            });
    }

    public set(groupKey: string, key: string, value: string): Promise<void> {
        if (!this.localStorageAvailable) {
            return Promise.reject<void>(new UserSettingsServiceError("Local storage is not supported"));
        }

        const combinedKey = StringExtensions.format(UserSettingsService.keyFormatter, groupKey, key);

        try {
            localStorage.setItem(combinedKey, value);
            return Promise.resolve();
        } catch (e) {
            // Most of the times, this is an out of storage exception (exceeds quota)
            return Promise.reject(e);
        }
    }

    public get(groupKey: string, key: string): Promise<string> {
        if (!this.localStorageAvailable) {
            return Promise.reject<string>(new UserSettingsServiceError("Local storage is not supported"));
        }

        const combinedKey = StringExtensions.format(UserSettingsService.keyFormatter, groupKey, key);
        const value = localStorage.getItem(combinedKey);
        if (value === null) return Promise.reject<string>(new UserSettingsServiceError("Key not found"));

        return Promise.resolve(value);
    }

    public delete(groupKey: string, key: string): Promise<void> {
        if (!this.localStorageAvailable) {
            return Promise.reject(new UserSettingsServiceError("Local storage is not supported"));
        }

        const combinedKey = StringExtensions.format(UserSettingsService.keyFormatter, groupKey, key);
        localStorage.removeItem(combinedKey);

        return Promise.resolve();
    }

    public hasKey(groupKey: string, key: string): Promise<boolean> {
        if (!this.localStorageAvailable) {
            return Promise.resolve(false);
        }

        return this.getKeys(groupKey).then((x) => x.indexOf(key) !== -1);
    }

    public getKeys(groupKey: string): Promise<string[]> {
        if (!this.localStorageAvailable) {
            return Promise.resolve(<string[]>[]);
        }

        const subStringIndex = groupKey.length + 1;
        const keys = <string[]>[];
        for (let i = 0; i < localStorage.length; i++) {
            const combinedKey = localStorage.key(i);
            if (combinedKey.startsWith(groupKey)) {
                keys.push(combinedKey.substr(subStringIndex));
            }
        }

        return Promise.resolve(keys);
    }

    private parseToString(value: unknown, targetObject: unknown, targetProperty: string): string {
        if (value == undefined) {
            return undefined;
        }

        const propertySettings = getUserSettingSettings(targetObject as object, targetProperty);
        if (propertySettings && propertySettings.toStringConverter) {
            return propertySettings.toStringConverter(value);
        } else if (propertySettings) {
            const parser =
                propertySettings && propertySettings.objectType && !propertySettings.parser
                    ? ParserCatalog.getParser(propertySettings.objectType)
                    : propertySettings.parser;

            if (parser) {
                return parser.toWorkflowProperty(value);
            }
        }

        return JSON.stringify(value);
    }

    private parseFromString(str: string, targetObject: unknown, targetProperty: string): unknown {
        if (str == undefined) {
            return undefined;
        }

        const propertySettings = getUserSettingSettings(targetObject as object, targetProperty);
        if (propertySettings && propertySettings.fromStringConverter) {
            return propertySettings.fromStringConverter(str);
        } else if (propertySettings) {
            const parser =
                propertySettings && propertySettings.objectType && !propertySettings.parser
                    ? ParserCatalog.getParser(propertySettings.objectType)
                    : propertySettings.parser;

            if (parser) {
                return parser.fromWorkflowProperty(str);
            }
        }

        // Due to compatibility reasons, try to remove as much as possible
        let result = JSON.parse(str);
        let i = 0;
        try {
            while (result != JSON.parse(result) && i++ < 100) {
                result = JSON.parse(result);
            }
        } catch (e) {
            // We parsed as much as possible
        }
        return result;
    }
}
